/**
 * Poll Creation Modal Component
 * Allows users to create polls with customizable settings
 */

import React, { useState, useEffect } from 'react';
import { useCreatePoll } from '../../hooks/usePolls';
import type { CreatePollRequest } from '../../types/api.types';
import { useDialogA11y } from '@/hooks/useDialogA11y';

interface PollCreationModalProps {
  messageId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export const PollCreationModal: React.FC<PollCreationModalProps> = ({
  messageId,
  onClose,
  onSuccess,
}) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [isMultipleChoice, setIsMultipleChoice] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [duration, setDuration] = useState<'1h' | '24h' | '7d' | null>('24h');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { dialogRef, titleId } = useDialogA11y(onClose);

  const createPollMutation = useCreatePoll();

  // Validation
  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Question validation
    if (!question.trim()) {
      newErrors.question = 'Въпросът е задължителен';
    } else if (question.length > 500) {
      newErrors.question = `Въпросът е твърде дълъг (${question.length}/500 символа)`;
    }

    // Options validation
    const validOptions = options.filter((opt) => opt.trim());
    if (validOptions.length < 2) {
      newErrors.options = 'Моля, въведете поне 2 опции';
    } else if (validOptions.length > 10) {
      newErrors.options = 'Максимум 10 опции са позволени';
    }

    // Individual option validation
    options.forEach((opt, index) => {
      if (opt.trim() && opt.length > 255) {
        newErrors[`option_${index}`] = `Твърде дълга (${opt.length}/255)`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validate()) return;

    const pollData: CreatePollRequest = {
      messageId,
      question: question.trim(),
      options: options.filter((opt) => opt.trim()),
      isMultipleChoice,
      isAnonymous,
      duration,
    };

    try {
      await createPollMutation.mutateAsync(pollData);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to create poll:', error);
      setErrors({ submit: 'Грешка при създаване на анкета. Моля, опитайте отново.' });
    }
  };

  // Add option
  const addOption = () => {
    if (options.length < 10) {
      setOptions([...options, '']);
    }
  };

  // Remove option
  const removeOption = (index: number) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  // Update option text
  const updateOption = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50" onClick={onClose}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="bg-white dark:bg-dark-bg-elevated rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-border-primary">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Създаване на анкета</h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary transition-colors"
            aria-label="Затвори"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Question Input */}
          <div>
            <label htmlFor="question" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Въпрос:
            </label>
            <textarea
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Какво предпочитате?"
              className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                errors.question ? 'border-red-500' : 'border-gray-300 dark:border-dark-border-primary'
              }`}
              rows={3}
              maxLength={500}
            />
            <div className="flex items-center justify-between mt-1">
              <span
                className={`text-xs ${
                  question.length > 450 ? 'text-red-600' : 'text-gray-500 dark:text-dark-text-muted'
                }`}
              >
                {question.length}/500 символа
              </span>
              {errors.question && (
                <span className="text-xs text-red-600">{errors.question}</span>
              )}
            </div>
          </div>

          {/* Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">Опции:</label>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-dark-text-muted w-6">{index + 1}.</span>
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    placeholder={`Опция ${index + 1}`}
                    className={`flex-1 px-3 py-2 border rounded-md bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors[`option_${index}`] ? 'border-red-500' : 'border-gray-300 dark:border-dark-border-primary'
                    }`}
                    maxLength={255}
                  />
                  {options.length > 2 && (
                    <button
                      onClick={() => removeOption(index)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                      aria-label="Премахни опция"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
            {errors.options && (
              <p className="text-xs text-red-600 mt-1">{errors.options}</p>
            )}
            {options.length < 10 && (
              <button
                onClick={addOption}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                + Добави опция
              </button>
            )}
          </div>

          {/* Settings */}
          <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-dark-border-primary">
            {/* Multiple Choice Toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isMultipleChoice}
                onChange={(e) => setIsMultipleChoice(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-dark-text-secondary">Позволи множествен избор</span>
            </label>

            {/* Anonymous Toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-dark-text-secondary">Анонимно гласуване</span>
            </label>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Продължителност:
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="duration"
                  checked={duration === '1h'}
                  onChange={() => setDuration('1h')}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-dark-text-secondary">1 час</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="duration"
                  checked={duration === '24h'}
                  onChange={() => setDuration('24h')}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-dark-text-secondary">24 часа</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="duration"
                  checked={duration === '7d'}
                  onChange={() => setDuration('7d')}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-dark-text-secondary">7 дни</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="duration"
                  checked={duration === null}
                  onChange={() => setDuration(null)}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-dark-text-secondary">Без изтичане</span>
              </label>
            </div>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
              <p className="text-sm text-red-800 dark:text-red-300">{errors.submit}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-dark-border-primary">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-secondary bg-gray-100 dark:bg-dark-bg-secondary rounded-md hover:bg-gray-200 dark:hover:bg-dark-bg-hover transition-colors"
            disabled={createPollMutation.isPending}
          >
            Отказ
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={createPollMutation.isPending}
          >
            {createPollMutation.isPending ? 'Създаване...' : 'Създай анкета'}
          </button>
        </div>
      </div>
    </div>
  );
};
